import type {
  CrawlJob,
  CrawlSchedule,
  CrawlStatus,
  WithResourceId,
} from '../../types/knowledge';
import type { DirectoryUser } from '../../types/user';
import type { I18nContextValue } from '../../types/i18nContext';
import { formatDirectoryUserDisplayName } from '../user/userDisplay';
import { formatTimestamp } from '../../utils/formatUtils';

type Translate = I18nContextValue['translate'];

export function formatScheduleInterval(
  schedule: CrawlSchedule,
  translate: Translate,
): string {
  if (!schedule.scheduleInterval) {
    return translate('knowledge.notAvailable');
  }
  if (schedule.scheduleInterval === 'CUSTOM') {
    const hours = schedule.customIntervalHours ?? 24;
    return translate('knowledge.crawlScheduleInterval.customEveryHours', {
      count: String(hours),
    });
  }
  return translate(
    `knowledge.crawlScheduleInterval.${schedule.scheduleInterval.toLowerCase()}`,
  );
}

export function formatCrawlStatus(
  status: CrawlStatus,
  translate: Translate,
): string {
  return translate(`knowledge.crawlStatus.${status}`);
}

export function formatCrawlMeta(
  crawl: WithResourceId<CrawlJob>,
  options: {
    translate: Translate;
    locale: string;
    crawlScheduleByJobId: Map<string, WithResourceId<CrawlSchedule>>;
    directoryUsersById: Map<string, DirectoryUser>;
  },
): string {
  const { translate, locale, crawlScheduleByJobId, directoryUsersById } =
    options;
  const urlCount = crawl.selectedUrlCount ?? crawl.mappedUrlCount ?? 0;
  const depth =
    crawl.maxDepth != null
      ? String(crawl.maxDepth)
      : translate('knowledge.notAvailable');
  const maxPages =
    crawl.maxPages != null
      ? String(crawl.maxPages)
      : translate('knowledge.notAvailable');
  const schedule = crawlScheduleByJobId.get(crawl.id);
  const scheduleMeta =
    crawl.status === 'COMPLETED' && schedule
      ? schedule.active
        ? translate('knowledge.crawlScheduleMetaActive', {
            interval: formatScheduleInterval(schedule, translate),
          })
        : translate('knowledge.crawlScheduleMetaPaused')
      : null;
  const startedByName = crawl.startedBy
    ? formatDirectoryUserDisplayName(
        directoryUsersById.get(crawl.startedBy),
        translate('knowledge.members.unknownUser'),
      )
    : null;
  const startedByMeta =
    startedByName && crawl.startedAt
      ? translate('knowledge.crawlStatStartedByAt', {
          name: startedByName,
          date: formatTimestamp(crawl.startedAt, locale),
        })
      : startedByName
        ? translate('knowledge.crawlMetaStartedBy', { name: startedByName })
        : null;
  const lastSuccessfulRunAt =
    crawl.status === 'COMPLETED'
      ? (crawl.completedAt ?? schedule?.lastRunAt ?? null)
      : null;
  const lastSuccessfulRunMeta = lastSuccessfulRunAt
    ? translate('knowledge.crawlStatLastSuccessfulRun', {
        date: formatTimestamp(lastSuccessfulRunAt, locale),
      })
    : null;

  return [
    translate('knowledge.crawlMetaUrls', { count: String(urlCount) }),
    translate('knowledge.crawlMetaDepth', { value: depth }),
    translate('knowledge.crawlMetaMaxPages', { value: maxPages }),
    startedByMeta,
    lastSuccessfulRunMeta,
    scheduleMeta,
  ]
    .filter(Boolean)
    .join(' · ');
}
